// src/pages/ViewStores.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import StoreService from "../services/ProjectStoreService";
import ProjectService from "../services/ProjectService";
import Breadcrumb from "../components/Breadcrumb";
import Loading from "../components/loading";
import ScreenData from "../config/ScreenData";
import usePermission from "../utils/UsePermission";
import UnAuthorized from "../views/Unathorized";
import StorageService from "../services/StorageService";

export default function ViewStores() {
  const [stores, setStores] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const messageTimer = useRef(null);

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const { permission, can } = usePermission(
    ScreenData.Project_Stores,
    showMessage
  );

  const closeMessage = () => {
    setMessage("");
    if (messageTimer.current) clearTimeout(messageTimer.current);
  };

  const fetchStores = async () => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany()?.companyId;
      const res = await StoreService.getAll(1, 20, companyId);
      const list = res?.data || res || [];

      const formatted = list.map((s) => ({
        ...s,
        isActive: s.status === "Active",
      }));

      setStores(formatted);
    } catch (err) {
      console.error("Error fetching stores:", err);
      showMessage("Failed to fetch stores", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const projRes = await ProjectService.getAllProjects();
      const list = Array.isArray(projRes) ? projRes : projRes?.data || [];
      setProjects(list);
    } catch (err) {
      console.error("Error fetching projects:", err);
      showMessage("Failed to fetch projects", "error");
    }
  };

  useEffect(() => {
    if (permission === null) return;
    if (permission.canView !== 1) {
      setLoading(false);
      return;
    }
    fetchStores();
    fetchProjects();
  }, [permission]);

  const getProjectName = (projectId) => {
    if (!projects || projects.length === 0) return "-";
    const idNum = Number(projectId);
    const proj = projects.find((p) => Number(p.id) === idNum);
    return proj ? proj.projectName || proj.name || "-" : "-";
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    setSelected(e.target.checked ? stores.map((s) => s.id) : []);
  };

  const handleAdd = () => {
    if (!can("canAdd")) {
      showMessage("You don't have permission to add", "error");
      return;
    }
    navigate("/cpnl/projectstore/add");
  };

  const handleModify = () => {
    if (!can("canModify")) {
      showMessage("You don't have permission to modify", "error");
      return;
    }
    if (selected.length === 1) {
      // const store = stores.find((s) => s.id === selected[0]);
      // localStorage.setItem("editStore", JSON.stringify(store));
      navigate(`/cpnl/projectstore/add/${selected[0]}`);
    } else if (selected.length === 0) {
      showMessage("Please select a store to modify.", "error");
    } else {
      showMessage("Please select only one store to modify.", "error");
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    if (!can("canModify")) {
      showMessage("You don't have permission to modify status", "error");
      return;
    }
    const newStatusText = currentStatus ? "Inactive" : "Active";

    const confirmed = window.confirm(
      `Are you sure you want to Mark this Store as ${newStatusText}?`
    );
    if (!confirmed) return;

    try {
      await StoreService.updateStatus(id);
      setStores((prev) =>
        prev.map((s) => (s.id === id ? { ...s, isActive: !currentStatus } : s))
      );

      showMessage(`Store status updated to ${newStatusText}`, "success");
    } catch (error) {
      console.error("Status update failed:", error);
      showMessage("Failed to update status", "error");
    }
  };

  if (permission && permission.canView !== 1) {
    return <UnAuthorized />;
  }

  return (
    <>
      <Breadcrumb
        pageTitle=" Project Store"
        secondTitle="Projects"
      ></Breadcrumb>
      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {message && (
            <div
              className={`alert ${
                msgType === "success" ? "alert-success" : "alert-danger"
              } d-flex justify-content-between align-items-center w-100 mb-2 p-2`}
            >
              <span>{message}</span>
              <button
                type="button"
                className="btn-close"
                onClick={closeMessage}
              ></button>
            </div>
          )}

          <div className="mb-3 pb-2 border-bottom">
            {can("canAdd") && (
              <button
                className="d-inline-flex btn btn-success me-2"
                onClick={handleAdd}
                disabled={loading}
              >
                <i className="ti ti-plus me-1"></i> Add
              </button>
            )}
            {can("canModify") && (
              <button
                className="d-inline-flex btn btn-warning me-2"
                onClick={handleModify}
                disabled={loading}
              >
                <i className="ti ti-pencil me-1"></i> Modify
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selected.length === stores.length && stores.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Store Name</th>
                  <th>Store Code</th>
                  <th>Project</th>
                  <th>From KMS</th>
                  <th>To KMS</th>
                  <th>Status</th>
                  {/* <th>Action</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Loading />
                    </td>
                  </tr>
                ) : stores.length > 0 ? (
                  stores.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selected.includes(s.id)}
                          onChange={() => handleSelect(s.id)}
                        />
                      </td>
                      <td>{s.storeName}</td>
                      <td>{s.storeCode}</td>
                      <td>{getProjectName(s.projectId)}</td>
                      <td>{s.fromKMS ?? "-"}</td>
                      <td>{s.toKMS ?? "-"}</td>
                      <td>
                        <span
                          className={`badge ${
                            s.isActive ? "bg-success" : "bg-secondary"
                          }`}
                          onClick={() => handleToggleStatus(s.id, s.isActive)}
                          style={{ cursor: can("canModify") ? "pointer" : "not-allowed" }}
                        >
                          {s.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {/* <td>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() => toggleStatus(s.id)}
                      >
                        {s.status === "Active" ? "Inactivate" : "Activate"}
                      </button>
                    </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No stores found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
